import { SmartMeterColumn } from "./smartMeterColumn"
import {
  ISmartMeterColumn,
  SmartMeterDistinctValues,
} from "./smartMeterColumn.types"
import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import { GetSmartMeterColumnValuesQuery } from "@/api/hasura/generated/graphql"
import {
  modelNameMapping,
  manufacturerNameMapping,
} from "@/constants/smartMeters"

export type SmartMeterColumnRaw = GetSmartMeterColumnValuesQuery

const getDistinctValues = (
  data: SmartMeterColumnRaw,
): SmartMeterDistinctValues => {
  const result: SmartMeterDistinctValues = {}

  const rows = data.cs__soe_dashboard_total
  if (!rows?.length) return result

  rows.forEach((row) => {
    Object.entries(row).forEach(([column, value]) => {
      if (!value) return
      result[column] = result[column] || {}
      result[column][value] = mapColumnValue(column, value)
    })
  })

  return result
}

const mapColumnValue = (column: string, value: string): string => {
  const columnMappings: Record<string, (code: string) => string> = {
    model: getModelName,
    manufacturer: getManufacturerName,
  }

  return columnMappings[column] ? columnMappings[column](value) : value
}

const getModelName = (modelCode: string): string => {
  const mappedName = modelNameMapping[modelCode]
  return mappedName ? `${modelCode} - ${mappedName}` : modelCode
}

const getManufacturerName = (manufacturerCode: string): string => {
  const mappedName = manufacturerNameMapping[manufacturerCode]
  return mappedName ? `${manufacturerCode} - ${mappedName}` : manufacturerCode
}

export const SmartMeterColumnParser: IParser<
  ISmartMeterColumn,
  SmartMeterColumnRaw
> = {
  toDomain: (data): ISmartMeterColumn => {
    try {
      const parsedData = getDistinctValues(data)

      return new SmartMeterColumn({
        errorCode: parsedData.error,
        errorStatus: parsedData.error_status,
        lastOrchestration: parsedData.last_orchestration,
        orchestrationStatus: parsedData.orchestration_status,
        serviceRequest: parsedData.srv,
        latestAlert: parsedData.latest_alert_code,
        latestAlertStatus: parsedData.latest_alert_status,
        firmwareStatus: parsedData.firmware_status,
        firmwareVersion: parsedData.firmware_version,
        gbcsVersion: parsedData.gbcs_version,
        manufacturer: parsedData.manufacturer,
        model: parsedData.model,
        paymentMode: parsedData.payment_mode,
        region: parsedData.region,
        smetsVersion: parsedData.smets_version,
        variant: parsedData.variant,
      })
    } catch (error) {
      throw new ParseError(error)
    }
  },
}
